"use client";
import { useEffect, useState } from 'react';
import { Header } from '@/pages/home/sections/header';
import { Footer } from '@/pages/home/sections/footer';
import { StandardCTA } from '@/components/standard-cta';
import { motion } from 'framer-motion';

type WordPressPost = {
  id: number;
  slug: string;
  link: string;
  date: string;
  title: { rendered: string };
  excerpt: { rendered: string };
  content: { rendered: string };
  _embedded?: {
    author?: Array<{ name?: string }>;
    'wp:featuredmedia'?: Array<{ source_url?: string }>;
  };
};

export default function BlogPage() {
  const [posts, setPosts] = useState<WordPressPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const apiBase = import.meta.env.VITE_API_URL;

        if (!apiBase) {
          throw new Error('VITE_API_URL is not defined');
        }

        const response = await fetch(`${apiBase}/posts?per_page=6&_embed`);

        if (!response.ok) {
          throw new Error(`Failed to fetch posts (status: ${response.status})`);
        }

        const data: WordPressPost[] = await response.json();
        setPosts(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error fetching posts');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const blogLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Dhikr Institute Blog',
    description:
      'Practical guidance on Qur\'an memorization, tajwīd, Arabic learning and Islamic Studies for all ages.',
    url: 'https://example.com/blog',
  };

  return (
    <div className="min-h-screen bg-[#FCF8F1]">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-12 sm:pt-40 sm:pb-16 lg:pt-48 lg:pb-20">
          <div className="w-[90%] max-w-7xl mx-auto">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-3 py-1 text-sm font-medium text-[#245E51] bg-[#A8FF51]/20 rounded-full border border-[#A8FF51]/30">
                Blog & Resources
              </div>
              <motion.h1
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.75, ease: [0.23, 0.68, 0.32, 0.97] }}
                className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-gray-900"
              >
                Learn Smarter. Grow Deeper.{' '}
                <span className="text-transparent bg-gradient-to-r from-[#245E51] to-[#A8FF51] bg-clip-text">Qur'an • Arabic • Islamic Studies</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.06, ease: [0.23, 0.68, 0.32, 0.97] }}
                className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl leading-relaxed"
              >
                Actionable advice and step-by-step frameworks inspired by leading online tutoring models—personalized, structured and results-driven.
              </motion.p>
            </div>

            {/* Category chips */}
            <div className="mt-10 flex flex-wrap gap-3 text-sm">
              {['Qur\'an', 'Tajwīd', 'Arabic', 'Hifz', 'Kids', 'Adults', 'Islamic Studies', 'Guides'].map((c) => (
                <span
                  key={c}
                  className="px-3 py-1 rounded-full border border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:shadow-sm cursor-default"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Grid */}
        <section className="pb-6">
          <div className="w-[90%] max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading && (
              <div className="col-span-full text-center text-gray-500">Loading posts...</div>
            )}

            {error && !loading && (
              <div className="col-span-full text-center text-red-500">
                حدث خطأ أثناء جلب المقالات: {error}
              </div>
            )}

            {!loading && !error && posts.length === 0 && (
              <div className="col-span-full text-center text-gray-500">
                لا توجد مقالات متاحة حالياً.
              </div>
            )}

            {!loading && !error &&
              posts.map((post, i) => {
                const featuredImage =
                  post._embedded?.['wp:featuredmedia']?.[0]?.source_url ??
                  'https://via.placeholder.com/640x400/FCF8F1/245E51?text=Dhikr+Institute';
                const authorName = post._embedded?.author?.[0]?.name ?? 'Dhikr Institute';

                return (
                  <motion.a
                    key={post.id}
                    href={post.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.6, delay: i * 0.05, ease: [0.23, 0.68, 0.32, 0.97] }}
                    className="group rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={featuredImage}
                        alt={post.title.rendered.replace(/<[^>]+>/g, '')}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5 space-y-3">
                      <h3 className="font-semibold text-gray-900 tracking-tight group-hover:text-[#245E51] line-clamp-2">
                        <span dangerouslySetInnerHTML={{ __html: post.title.rendered }} />
                      </h3>
                      <div className="text-sm text-gray-600 line-clamp-3">
                        <span dangerouslySetInnerHTML={{ __html: post.excerpt.rendered }} />
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span className="font-medium text-gray-700">{authorName}</span>
                        <time dateTime={new Date(post.date).toISOString()}>
                          {new Date(post.date).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </time>
                      </div>
                    </div>
                  </motion.a>
                );
              })}
          </div>
        </section>

        <StandardCTA />

        {/* SEO JSON-LD */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(blogLd) }} />
      </main>
      <Footer />
    </div>
  );
}
